import React from 'react';
import {
  faBan,
  faChartLine,
  faCheck,
  faCog,
  faCommentSlash,
  faDotCircle,
  faEdit,
  faFlagCheckered,
  faPauseCircle,
  faStickyNote,
  faTrash,
  faUserMd
} from '@fortawesome/pro-solid-svg-icons';

import { Col, Container, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import { Tabs, Tab } from '../../molecules/tab';
import Button from '../../molecules/button';
import ErrorBoundary from '../../molecules/error-boundary';
import Modal from '../../organisms/modal';
import StatusBadge from '../../organisms/status/status-badge';
import PatientEvents from '../../organisms/patient/events';
import StatusIndicator from '../../organisms/status/status-indicator';

import { AppKey } from '../../../libs/apps';
import { FormatContact } from '../../../libs/contact';
import { RouteMapping } from '../../../libs/route';

import { TriageCategory, TriagedApp } from '../../../contexts/triage/types';
import { GetSecondaryCategoryForApp } from '../../../contexts/triage/utils';
import { useApps } from '../../../contexts/apps';
import { useTriage } from '../../../contexts/triage';
import { useMetaData } from '../../../contexts/metadata';
import { useTriagePatient } from '../../../contexts/triage/patient';
import {
  getPausedApps,
  isFullPause,
  getSuspensionDatesForApp
} from '../../../libs/tasks';

import ProgramData from './data';
import ProgramPrescription from './prescription';
import HeaderBanner from './banner';
import moment from 'moment-timezone';
import { IPatient, ReprescribePatient } from '../../../libs/patient';
import { ISchedule } from '../../../libs/schedule';
import { FormatDate, GetCurrentTZ } from '../../../libs/time';
import { OtherProgramProps } from '../survey/quality-survey';

const getRevokedDate = (data: TriagedApp) => {
  if (!data) {
    return;
  }
  if (!data.waiver) {
    return;
  }
  if (!data.waiver.revokedAt) {
    return;
  }
  return moment(data.waiver.revokedAt).format('MM/DD/YY');
};

interface OptInProps {
  appKey: AppKey;
  patientId: IPatient['id'];
  useReconsent?: boolean;
}

const OptIn = ({
  appKey,
  patientId,
  useReconsent = false
}: OptInProps): JSX.Element => {
  const { terms } = useMetaData();
  const words = useReconsent
    ? {
        upper: 'Reconsent',
        lower: 'reconsent'
      }
    : {
        upper: 'Opt-In',
        lower: 'opt-in'
      };
  const represcribe = () => {
    Modal.confirm({
      title: `${words.upper} ${terms.patient.capitalized}`,
      content: `This will attempt to ${words.lower} the ${terms.patient.capitalized} again, are you sure you want to continue?`,
      onOk: () => {
        ReprescribePatient({
          app: appKey,
          patientId
        }).then((response) => {
          if (response.status !== 'OK') {
            Modal.error({
              title: `Error During ${words.upper}`,
              content: response.message
            });
            return;
          }
          Modal.info({
            title: 'Next Steps',
            content: response.message
          });
        });
      }
    });
  };

  return (
    <Button
      className="ml-1 text-sans-sm"
      intent="primary"
      onClick={represcribe}
      variant="minimal"
      bare
    >
      <Icon icon={faCheck} /> Opt-In {terms.patient.capitalized}
    </Button>
  );
};

interface ResubscribeProps {
  patientId: IPatient['id'];
}

const Represcribe = ({ patientId }: ResubscribeProps): JSX.Element => {
  return (
    <Button
      className="ml-1 text-sans-sm"
      linkTo={`/prescribe/?id=${patientId}`}
      intent="primary"
      variant="minimal"
      bare
    >
      <Icon icon={faEdit} /> Re-prescribe
    </Button>
  );
};

type ProgramBannersProps = Pick<
  PatientProgramProps,
  'appKey' | 'data' | 'patientId'
>;

interface CompletedProgramBannerProps {
  patientId: PatientProgramProps['patientId'];
  schedule: ISchedule;
  hasEndedSchedule: boolean;
}

const CompletedProgramBanner = ({
  patientId,
  schedule,
  hasEndedSchedule
}: CompletedProgramBannerProps): JSX.Element => {
  const { isReadOnly } = useMetaData();
  let description =
    'This program has reached its prescribed end date. It will become a "Past Program" 2 weeks after its end date.';
  if (schedule) {
    description = `This program reached its end date of ${FormatDate(
      schedule.end
    )}.`;
    if (!hasEndedSchedule) {
      const twoWeeksDate = FormatDate(moment(schedule.end).add(2, 'weeks'));
      description += ` It will be shown in "Past Programs" after ${twoWeeksDate}.`;
    }
  }
  return (
    <HeaderBanner icon={faFlagCheckered} title="Program Ended">
      {description}
      {!isReadOnly && <Represcribe patientId={patientId} />}
    </HeaderBanner>
  );
};

type CanceledProgramBannerProps = CompletedProgramBannerProps;

const CanceledProgramBanner = ({
  patientId,
  schedule,
  hasEndedSchedule
}: CanceledProgramBannerProps): JSX.Element => {
  const { isReadOnly } = useMetaData();
  let description = 'The prescription for this program was removed.';
  if (schedule) {
    description = `The prescription for this program was removed on ${FormatDate(
      schedule.canceledAt
    )}.`;
    if (!hasEndedSchedule) {
      const twoWeeksDate = FormatDate(
        moment(schedule.canceledAt).add(2, 'weeks')
      );
      description += ` It will be shown in "Past Programs" after ${twoWeeksDate}.`;
    }
  }
  return (
    <HeaderBanner icon={faTrash} className="bg-light-orange" title="Removed">
      {description}
      {!isReadOnly && <Represcribe patientId={patientId} />}
    </HeaderBanner>
  );
};

const ProgramBanners = ({
  appKey,
  data,
  patientId
}: ProgramBannersProps): JSX.Element => {
  const { terms } = useMetaData();
  const { isReadOnly } = useMetaData();
  const secondaryCategory = GetSecondaryCategoryForApp(data, data.category);
  const revokedDate = getRevokedDate(data);
  // Check if this specific program is paused
  const { patientData } = useTriagePatient();
  const patient = patientData?.patient;
  const tasks = patient?.tasks || [];
  const pausedAppKeys = getPausedApps(tasks);
  const fullPause = isFullPause(tasks);
  const isProgramPaused =
    patient?.isSuspended &&
    (fullPause || pausedAppKeys.includes(appKey as string));
  const appSuspensionDates = getSuspensionDatesForApp(tasks, appKey as string);
  let schedule = data.schedule;
  let hasEndedSchedule = false;
  if (data.endedSchedule) {
    schedule = data.endedSchedule;
    hasEndedSchedule = true;
  }
  return (
    <React.Fragment>
      {isProgramPaused && (
        <HeaderBanner
          icon={faPauseCircle}
          title="Program Paused"
          className="bg-light-orange"
        >
          This program is currently paused
          {appSuspensionDates?.endDate
            ? ` until ${moment
                .tz(appSuspensionDates.endDate, GetCurrentTZ())
                .format('YYYY-MM-DD')}`
            : ''}
          . No communications will be sent for this program during the pause.
        </HeaderBanner>
      )}
      {secondaryCategory === TriageCategory.Canceled && (
        <CanceledProgramBanner
          patientId={patientId}
          schedule={schedule}
          hasEndedSchedule={hasEndedSchedule}
        />
      )}
      {secondaryCategory === TriageCategory.Inactive && (
        <HeaderBanner icon={faCommentSlash} title="Unconsented">
          {revokedDate !== undefined && revokedDate.length > 0 ? (
            <>
              The {terms.patient.default} declined consent to getting messages
              from this program on {revokedDate}.
              {!isReadOnly && (
                <OptIn appKey={appKey} patientId={patientId} useReconsent />
              )}
            </>
          ) : (
            <>
              {terms.patient.capitalized} has not consented to getting messages
              from this program.
              {!isReadOnly && (
                <OptIn appKey={appKey} patientId={patientId} useReconsent />
              )}
            </>
          )}
        </HeaderBanner>
      )}
      {secondaryCategory === TriageCategory.Revoked && (
        <HeaderBanner icon={faBan} title="Opted Out">
          {revokedDate !== undefined && revokedDate.length > 0 ? (
            <>
              The {terms.patient.default} opted-out of this program on{' '}
              {revokedDate}.
              {!isReadOnly && <OptIn appKey={appKey} patientId={patientId} />}
            </>
          ) : (
            <>
              The {terms.patient.default} has opted-out of this program.
              {!isReadOnly && <OptIn appKey={appKey} patientId={patientId} />}
            </>
          )}
        </HeaderBanner>
      )}
      {secondaryCategory === TriageCategory.Completed && (
        <CompletedProgramBanner
          patientId={patientId}
          schedule={schedule}
          hasEndedSchedule={hasEndedSchedule}
        />
      )}
      {secondaryCategory === TriageCategory.Empty && (
        <HeaderBanner icon={faDotCircle} title="No responses">
          The {terms.patient.default} has consented to this program but there
          are no recent scheduled communications.
        </HeaderBanner>
      )}
    </React.Fragment>
  );
};

interface PatientProgramProps {
  appKey: AppKey;
  data: TriagedApp | OtherProgramProps;
  patientId: number;
  alertAppKeys?: string[];
  eventAppKeys?: string[];
  chartReflowTrigger: boolean;
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (isExpanded: boolean) => void;
  routes?: RouteMapping;
}

const PatientProgram: React.FC<PatientProgramProps> = ({
  appKey,
  data,
  patientId,
  alertAppKeys,
  eventAppKeys,
  chartReflowTrigger,
  isSidebarExpanded,
  setIsSidebarExpanded,
  routes
}): JSX.Element => {
  const { apps: appConfigs } = useApps().data || {};
  const config = appConfigs[appKey];
  const secondaryCategory = GetSecondaryCategoryForApp(data, data.category);
  const { getContact } = useTriage();
  const contactId =
    data.schedule && data.schedule.contactId
      ? data.schedule.contactId
      : data.contactId;
  const contact = getContact(contactId);
  let programName = config?.name;
  if ('name' in data) {
    programName = data?.name;
  }
  return (
    <Container fluid className="bg-white pb-4">
      <div>
        <ProgramBanners appKey={appKey} data={data} patientId={patientId} />
        <Container fluid>
          <Row align="center" justify="start">
            <Col width="auto">
              <h3>
                {secondaryCategory !== data.category ? (
                  <StatusBadge category={secondaryCategory} />
                ) : null}
                <StatusIndicator
                  category={data.category}
                  minimal={true}
                  title={false}
                />
                {programName}
              </h3>
            </Col>
            {contact != null && (
              <Col width="auto">
                <Icon icon={faUserMd} /> {FormatContact(contact)}
              </Col>
            )}
          </Row>
        </Container>
      </div>
      <Tabs id="program">
        <Tab
          id={`${appKey}-data`}
          title={
            <span>
              <Icon className="mr-2 text-primary" icon={faChartLine} /> Program
              Data
            </span>
          }
          className="px-2-important"
          panel={
            <ProgramData
              appKey={appKey}
              alertAppKeys={alertAppKeys}
              isSidebarExpanded={isSidebarExpanded}
              setIsSidebarExpanded={setIsSidebarExpanded}
              chartReflowTrigger={chartReflowTrigger}
              customProgram={data.customProgram}
            />
          }
        />
        <Tab
          id={`${appKey}-events`}
          title={
            <span>
              <Icon className="mr-2 text-primary" icon={faStickyNote} /> Event
              Notes
            </span>
          }
          panel={
            <PatientEvents
              appKey={appKey}
              appKeys={eventAppKeys}
              patientId={patientId}
            />
          }
        />
        <Tab
          id={`${appKey}-prescription`}
          title={
            <span>
              <Icon className="mr-2 text-primary" icon={faCog} /> Manage
              Prescription
            </span>
          }
          panel={
            <ErrorBoundary>
              <ProgramPrescription
                appKey={appKey}
                schedule={data.schedule}
                routes={routes}
                customProgram={data.customProgram}
              />
            </ErrorBoundary>
          }
        />
      </Tabs>
    </Container>
  );
};

export default PatientProgram;
